import React, { useState, useRef, useEffect, memo } from 'react'
import { Button } from 'antd'
import { ArrowsAltOutlined, PhoneOutlined } from '@ant-design/icons'
import moment from 'moment'
import { CallStatusString, CallInfo } from 'src/types/ai-assistant'
import { maskPhone } from '@library'

interface MinimizedWorkbenchProps {
  callInfo: CallInfo
  callStatus: CallStatusString
  sessionId: string
  isCallMute: boolean
  isCallHold: boolean
  setIsCallMute: (isCallMute: boolean) => void
  setIsCallHold: (isCallHold: boolean) => void
  onRestore: () => void
  position?: { x: number; y: number }
  onPositionChange?: (position: { x: number; y: number }) => void
  width: number
  height: number
}

const MinimizedWorkbench = memo(({
  callInfo,
  callStatus,
  sessionId,
  isCallMute,
  isCallHold,
  setIsCallMute,
  setIsCallHold,
  onRestore,
  position: externalPosition,
  onPositionChange,
  width,
  height,
}: MinimizedWorkbenchProps) => {
  // 时间计算状态
  const [callingSeconds, setCallingSeconds] = useState(0)
  const [talkingSeconds, setTalkingSeconds] = useState(0)
  const intervalCallingSecondsRef = useRef<NodeJS.Timeout | null>(null)
  const intervalTalkingSecondsRef = useRef<NodeJS.Timeout | null>(null)

  // 拖拽相关状态
  const [position, setPosition] = useState(
    externalPosition || { x: 0, y: 0 },
  ) // 位置由父组件管理
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number } | null>(null)
  const dragRef = useRef<HTMLDivElement>(null)

  // 初始化通话时间
  useEffect(() => {
    if (callInfo && callInfo.callTimestamp) {
      const timestamp = callInfo.callTimestamp
      const now = Date.now()
      const diffInSeconds = Math.floor((now - timestamp) / 1000)
      setTalkingSeconds(diffInSeconds)
    }
  }, [callInfo])

  // 呼叫中计时器
  useEffect(() => {
    intervalCallingSecondsRef.current = setInterval(() => {
      setCallingSeconds(prev => prev + 1)
    }, 1000)
    return () => {
      intervalCallingSecondsRef.current && clearInterval(intervalCallingSecondsRef.current)
      intervalCallingSecondsRef.current = null
      setCallingSeconds(0)
    }
  }, [])

  // 通话中计时器
  useEffect(() => {
    if (callStatus === 'talking') {
      setCallingSeconds(0)
      intervalCallingSecondsRef.current && clearInterval(intervalCallingSecondsRef.current)
      intervalCallingSecondsRef.current = null
      intervalTalkingSecondsRef.current = setInterval(() => {
        setTalkingSeconds(prev => prev + 1)
      }, 1000)
    }
    return () => {
      intervalTalkingSecondsRef.current && clearInterval(intervalTalkingSecondsRef.current)
      intervalTalkingSecondsRef.current = null
      setTalkingSeconds(0)
    }
  }, [callStatus])

  // 获取格式化的通话状态文本
  const getCallStatusText = () => {
    if (callStatus === 'calling') {
      return `呼叫中 (${moment.utc(moment.duration(callingSeconds, 'seconds').asMilliseconds()).format('HH:mm:ss')})`
    } else {
      return `通话中 (${moment.utc(moment.duration(talkingSeconds, 'seconds').asMilliseconds()).format('HH:mm:ss')})`
    }
  }

  // 拖拽事件处理
  const handleMouseDown = (e: React.MouseEvent) => {
    // 如果点击的是按钮区域或展开图标，不启动拖拽
    if ((e.target as HTMLElement).closest('.call-actions')
      || (e.target as HTMLElement).closest('.expand-icon')) {
      return
    }

    e.preventDefault()
    const rect = e.currentTarget.getBoundingClientRect()
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    })
  }

  const handleMouseMove = (e: MouseEvent) => {
    if (!dragOffset) return

    const newX = e.clientX - dragOffset.x
    const newY = e.clientY - dragOffset.y

    // 限制拖拽范围，防止拖出视窗
    const maxX = window.innerWidth - width // 卡片宽度
    const maxY = window.innerHeight - height // 卡片高度

    const newPosition = {
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    }

    setPosition(newPosition)
    // 通知父组件位置变化
    onPositionChange?.(newPosition)
  }

  const handleMouseUp = () => {
    setDragOffset(null)
  }

  // 添加全局事件监听（仅在拖拽时）
  useEffect(() => {
    if (dragOffset) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.userSelect = 'none'
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.userSelect = ''
    }
  }, [dragOffset])

  // 同步外部位置到内部状态
  useEffect(() => {
    if (externalPosition) {
      setPosition(externalPosition)
    }
  }, [externalPosition])

  return (
    <div
      ref={dragRef}
      className="call-workbench-minimized"
      style={{
        left: position.x,
        top: position.y,
        cursor: dragOffset ? 'grabbing' : 'grab',
        userSelect: 'none',
        height,
        width,
      }}
      onMouseDown={handleMouseDown}
    >
      <div className="call-status-card">
        <div className="call-info-section">
          <div>
            {getCallStatusText()}
          </div>
          <div className="call-details">
            <div className="phone-number">{maskPhone(callInfo?.calleePhoneNumberText)}</div>
            <div className="carrier-info">{callInfo?.calleeLocation}</div>
          </div>
          <div className="caller-number">主叫号码：{callInfo?.callerPhoneNumberText}</div>
        </div>
        <div className="expand-icon">
          <ArrowsAltOutlined
            onClick={(e) => {
              e.stopPropagation()
              onRestore()
            }}
            style={{ fontSize: 20, border: '1px solid', borderRadius: 2, cursor: 'pointer' }}
          />
        </div>
      </div>
      <div className="call-actions" onClick={(e) => e.stopPropagation()}>
        <Button
          type="primary"
          danger
          shape="round"
          icon={<PhoneOutlined rotate={224} />}
          onClick={() => window.tccc.Call.hungUp({ sessionId })}
        >
          挂断
        </Button>
        {callStatus === 'talking' && (
          <>
            {isCallMute ? (
              <Button
                type="primary"
                shape="round"
                onClick={() => {
                  window.tccc.Call.unmuteMic({ sessionId })
                  setIsCallMute(false)
                }}
              >
                解除静音
              </Button>
            ) : (
              <Button
                type="primary"
                shape="round"
                onClick={() => {
                  window.tccc.Call.muteMic({ sessionId })
                  setIsCallMute(true)
                }}
              >
                静音
              </Button>
            )}
            {isCallHold ? (
              <Button
                type="primary"
                disabled={isCallMute}
                shape="round"
                onClick={() => {
                  window.tccc.Call.unHold({ sessionId })
                  setIsCallHold(false)
                }}
              >
                恢复
              </Button>
            ) : (
              <Button
                type="primary"
                disabled={isCallMute}
                shape="round"
                onClick={() => {
                  window.tccc.Call.hold({ sessionId })
                  setIsCallHold(true)
                }}
              >
                挂起
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  )
})

export default MinimizedWorkbench
